/**
 * The facts one "Delete share" confirm shows and sends (plan Task 7.16): the snapshot count (`confirmedSnapshotCount`
 * — main refuses `SnapshotsChanged` when the live count differs), the share soft-delete state and the protocol, read
 * through `azure-storage:share-properties`. A small per-confirm store: the summary starts the read while it is on
 * screen and cancels it when it leaves (a cancelled read is restarted on the next `ensureLoaded`); Reload re-reads;
 * Confirm waits for the read in flight before it sends anything.
 */
import { create, type StoreApi, type UseBoundStore } from 'zustand';
import type { FileShareProtocolState, StorageOperationError, StorageRecoverabilityState } from '../../../../../types/azureStorage';
import { startShareRequest, type ShareRequestHandle, type ShareValueOf } from '../shareApi';

export type ShareDeleteFacts = {
  status: 'loading' | 'ready' | 'failed';
  count?: number;
  countReason?: string;
  softDelete?: StorageRecoverabilityState;
  protocol: FileShareProtocolState;
  readError?: StorageOperationError;
  /** The last refused attempt (a `SnapshotsChanged` conflict offers Reload). */
  error?: StorageOperationError;
};
export type ShareDeleteSession = {
  store: UseBoundStore<StoreApi<ShareDeleteFacts>>;
  ensureLoaded: () => void;
  reload: () => void;
  loaded: () => Promise<void>;
  dispose: () => void;
};

export function createShareDeleteSession(args: { connectionId: string; share: string; protocol?: FileShareProtocolState }): ShareDeleteSession {
  const store = create<ShareDeleteFacts>(() => ({ status: 'loading', protocol: args.protocol ?? 'unknown' }));
  let handle: ShareRequestHandle<ShareValueOf<'shareProperties'>> | undefined;
  let done: Promise<void> = Promise.resolve();
  let settled = false;

  const load = () => {
    handle?.cancel();
    settled = false;
    store.setState({ status: 'loading', error: undefined, readError: undefined });
    const current = startShareRequest('shareProperties', { connectionId: args.connectionId, resource: { kind: 'share', share: args.share } });
    handle = current;
    done = current.promise.then((result) => {
      if (handle !== current) return;
      settled = true;
      if (!result.ok) { store.setState({ status: 'failed', readError: result.error, count: undefined }); return; }
      const { properties, recoverability } = result.value;
      const snapshots = recoverability.snapshots;
      store.setState({
        status: 'ready',
        protocol: properties.protocol !== 'unknown' ? properties.protocol : store.getState().protocol,
        softDelete: recoverability.shareSoftDelete,
        count: snapshots.state === 'known' ? snapshots.count : undefined,
        countReason: snapshots.state === 'unknown' ? snapshots.reason : undefined,
      });
    });
  };
  return {
    store,
    ensureLoaded: () => { if (!handle) load(); },
    reload: load,
    loaded: () => done,
    dispose: () => {
      if (!settled) { handle?.cancel(); handle = undefined; }
    },
  };
}
